import FunctionalButton from "components/layout/FunctionalButton";

import ACommercial from "assets/imgs/ARC/ACommercial.png";
import AIv2 from "assets/imgs/ARC/AIv2.png";
import { ReactComponent as ViewIcon } from "assets/svgs/View.svg";
import NetworkIcon from "components/layout/status/NetworkIcon";
import BatteryIcon from "components/layout/status/BatteryIcon";
import { HAS_VIEWER_COUNT } from "lib/consts/flags";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { ARC } from "providers/MultiARCsControlProvider";
import { useARC } from "providers/ARC/ARCProvider";
import { isUndefined } from "lodash";
import Card from "components/common/Card";
import ARCName from "components/ARC/ARCName";
import EditableARCName from "components/ARC/EditableARCName";
import ARCThumbnail from "components/ARC/ARCThumbnail";
import { useArcThumbnail } from "components/ARC/useArcThumbnail";
import { resizeImageToPngDataUrl } from "lib/utils/resizeImage";
import { useToast } from "providers/ToastProvider";
import { useOrgFleet } from "providers/OrgFleetProvider";
import type { ARCStatus } from "types/fleet";

const ALL_NETWORK_TYPES = ["LTE", "WiFi"] as const;

const getARCImage = (ARCName = "") => {
    if (ARCName.includes("AI"))
        return (
            <img className="max-h-[7.25rem] max-w-[15.25rem]" src={ARCName} alt="ARC Industrial" />
        );
    else if (ARCName.includes("AIv2"))
        return (
            <img
                className="max-h-[7.25rem] max-w-[15.25rem]"
                src={AIv2}
                alt="ARC Industrial ver.2"
            />
        );
    else
        return (
            <img
                className="max-h-[7.25rem] max-w-[15.25rem]"
                src={ACommercial}
                alt="ARC Commercial"
            />
        );
};

export type ARCCardProps = Omit<Omit<React.HTMLProps<HTMLAnchorElement>, "action">, "onClick"> & {
    arc: ARC;
    arcStatus: Partial<ARCStatus>;
    actionTitle?: string;
    action?: () => void;
    onClick?: (arc: ARC) => void;
    /** Fleet edit mode: shows the rename pencil and thumbnail upload. */
    editing?: boolean;
};

const ARCCard = forwardRef(
    (
        {
            onClick,
            arc,
            arcStatus,
            actionTitle = "Control",
            action,
            className = "",
            editing = false,
            ...otherProps
        }: ARCCardProps,
        ref
    ) => {
        const { selectARC } = useARC();
        // Renaming and thumbnails go through org_server, so they only exist in fleet mode.
        const orgFleet = useOrgFleet();
        const canEdit = editing && !!orgFleet;
        // Outside fleet mode there are no thumbnails, so show the default image.
        const storedThumbnail = useArcThumbnail(
            arc.serialNumber,
            orgFleet ? orgFleet.thumbnailHashes[arc.serialNumber] ?? null : null
        );
        // The picked image, shown while it uploads and until the stored copy has loaded.
        const [uploadPreview, setUploadPreview] = useState<string | null>(null);
        const [uploading, setUploading] = useState(false);
        useEffect(() => {
            // A loaded image (string) after an upload is the stored copy of that upload.
            if (storedThumbnail) setUploadPreview(null);
        }, [storedThumbnail]);
        const addToast = useToast()?.addToast;

        const uploadThumbnail = async (file: File) => {
            setUploading(true);
            try {
                const dataUrl = await resizeImageToPngDataUrl(file);
                setUploadPreview(dataUrl);
                await orgFleet.setArcThumbnail(arc.serialNumber, dataUrl);
            } catch (err) {
                setUploadPreview(null);
                console.error("[ARCCard] thumbnail upload failed", err);
                addToast?.({ type: "error", message: "Could not update the photo. Try another image." });
            } finally {
                setUploading(false);
            }
        };

        const removeThumbnail = async () => {
            setUploading(true);
            try {
                await orgFleet.setArcThumbnail(arc.serialNumber, null);
                // Drop a just-uploaded preview that hasn't been replaced by its stored copy yet.
                setUploadPreview(null);
            } catch (err) {
                console.error("[ARCCard] thumbnail removal failed", err);
                addToast?.({ type: "error", message: "Could not remove the photo. Try again." });
            } finally {
                setUploading(false);
            }
        };
        if (typeof action === "undefined") {
            action = () => selectARC(arc.serialNumber);
        }
        return (
            <Card
                className={`flex h-Card min-h-Card flex-col ${className}`}
                onClick={onClick && (() => onClick(arc))}
                propRef={ref as React.RefObject<HTMLElement>}
                {...otherProps}
            >
                <Card.Title className="flex">
                    <div className="flex min-w-0 grow items-center">
                        {canEdit ? (
                            <EditableARCName
                                serialNumber={arc.serialNumber}
                                name={arc.info.name}
                                onRename={(name) => orgFleet.renameArc(arc.serialNumber, name)}
                                nameClassName="text-h4"
                            />
                        ) : (
                            <ARCName serialNumber={arc.serialNumber} name={arc.info.name} nameClassName="text-h4" />
                        )}
                    </div>
                    {actionTitle && typeof action === "function" && (
                        <FunctionalButton
                            className="w-fit rounded-r10 bg-primary px-2 py-0 hover:bg-secondary disabled:bg-black40"
                            title={actionTitle}
                            disabled={arcStatus?.online !== true}
                            onClick={action}
                        />
                    )}
                </Card.Title>
                <Card.Content className="flex grow flex-col">
                    <div className="w-full grow">
                        <div className="flex h-5 w-full  grow items-center justify-between">
                            <p className="text-h5">
                                {arcStatus?.online === true ? "Online" : "Offline"}
                            </p>
                            <div className="flex items-center space-x-2 self-end">
                                {arcStatus?.networks !== undefined
                                    ? ALL_NETWORK_TYPES.map((t) => {
                                          const entry = arcStatus.networks!.find((n) => n.type === t);
                                          return (
                                              <NetworkIcon
                                                  key={t}
                                                  type={t}
                                                  status={entry ? (entry.signal ?? 100) : 0}
                                                  showType
                                              />
                                          );
                                      })
                                    : <NetworkIcon
                                          type={arcStatus?.networkType}
                                          status={Number(arcStatus?.networkStatus) || 0}
                                      />
                                }
                                <BatteryIcon batteryLevel={arcStatus?.batteryLevel} />
                            </div>
                        </div>
                        <div className="relative flex w-full flex-col items-center justify-start">
                            <ARCThumbnail
                                thumbnail={uploadPreview ?? storedThumbnail}
                                fallback={getARCImage(arc.serialNumber)}
                                editing={canEdit}
                                uploading={uploading}
                                onPick={uploadThumbnail}
                                onRemove={removeThumbnail}
                            />
                            {arc.info.payload && (
                                <div className="absolute top-0 flex w-full  justify-start">
                                    <p className="text-h5l">{arc.info.payload}</p>
                                </div>
                            )}
                            {HAS_VIEWER_COUNT && (
                                <div className="absolute bottom-0 flex w-full  items-center justify-start">
                                    <ViewIcon className="text-black40" />

                                    <p className="ml-2 text-h5l text-black40">
                                        {arcStatus?.viewerCount}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="w-full">
                        <div className="flex w-full  items-center justify-between">
                            <p className="text-h4">Main Robot</p>
                            {arc.info.robot && (
                                <div className="flex  items-center justify-between space-x-2">
                                    <NetworkIcon
                                        type={arcStatus?.robot?.networkType}
                                        status={Number(arcStatus?.robot?.networkStatus) || 0}
                                    />
                                    <BatteryIcon batteryLevel={arcStatus?.robot?.batteryLevel} />
                                </div>
                            )}
                        </div>
                        <div className="flex w-full  items-center justify-start rounded-r5 bg-black90 px-5">
                            {arc.info.robot ? (
                                <p className="text-h5 w-full text-black40">
                                    {arc.info.robot.serialNumber}
                                </p>
                            ) : (
                                <p className="text-h5 w-full text-warning">Not Connected</p>
                            )}
                        </div>
                    </div>
                </Card.Content>
            </Card>
        );
    }
);

export default ARCCard;
